import { useState, useEffect, useCallback, useRef } from 'react';
import { getSubmissions, updateSubmission, resolveImageUrl } from '../api/client';

interface Submission {
  id: string;
  sku?: string;
  name?: string;
  description?: string;
  notes?: string;
  tags?: string;
  // Submission row's status — kept in shipped iOS app's vocabulary
  // (processing|pending|approved|rejected). Don't display this directly.
  status: string;
  // The pin's real moderation_status (can include 'ai_approved').
  // This is the source of truth for admin display and filtering.
  pin_moderation_status?: string;
  imageUrl?: string;
  image_key?: string;
  store_id?: string;
  ai_analysis?: string;
  created_at: string;
  updated_at: string;
  reviewed_at?: string;
}

interface AIAnalysis {
  name: string;
  tags: string[];
  description?: string;
  confidence: number;
  series?: string;
  isLimitedEdition?: boolean;
  isPinGroup?: boolean;
  childPinCount?: number;
}

function SubmissionImage({ src, name }: { src?: string; name?: string }) {
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement>(null);

  const handleMouseEnter = () => {
    if (imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      setPreviewPos({ x: rect.right + 8, y: rect.top });
    }
    setShowPreview(true);
  };

  if (!src || imageError) {
    return (
      <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-sm flex-shrink-0">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={name || 'Submission'}
        className="w-8 h-8 rounded object-cover flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowPreview(false)}
        onError={() => setImageError(true)}
      />
      {showPreview && (
        <div
          className="fixed z-[9999] p-1.5 bg-white rounded-lg shadow-xl border border-gray-200 pointer-events-none"
          style={{ left: previewPos.x, top: previewPos.y }}
        >
          <img
            src={src}
            alt={name || 'Submission'}
            className="w-40 h-40 object-contain rounded"
          />
        </div>
      )}
    </>
  );
}

function SubmissionDetailPanel({
  submission,
  onClose,
  onUpdateStatus,
}: {
  submission: Submission;
  onClose: () => void;
  onUpdateStatus: (sub: Submission, status: string) => void;
}) {
  const aiAnalysis: AIAnalysis | null = submission.ai_analysis
    ? JSON.parse(submission.ai_analysis)
    : null;

  const parseTags = (tags?: string): string[] => {
    if (!tags) return [];
    try {
      return JSON.parse(tags);
    } catch {
      return [];
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Submission Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Image */}
          {submission.imageUrl && (
            <div className="flex justify-center">
              <img
                src={resolveImageUrl(submission.imageUrl)}
                alt={submission.name || 'Submission'}
                className="max-h-64 rounded-lg shadow-md object-contain"
              />
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Name</label>
              <div className="text-slate-800 font-medium">{submission.name || 'Untitled'}</div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">SKU</label>
              <div className="font-mono text-sm text-gray-600">{submission.sku || '-'}</div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
              <div>
                {(() => {
                  const displayStatus = submission.pin_moderation_status || submission.status;
                  return (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      displayStatus === 'approved' ? 'bg-green-100 text-green-700' :
                      displayStatus === 'ai_approved' ? 'bg-purple-100 text-purple-700' :
                      displayStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {displayStatus}
                    </span>
                  );
                })()}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Submitted</label>
              <div className="text-sm text-gray-600">{new Date(submission.created_at).toLocaleString()}</div>
            </div>
          </div>

          {submission.description && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
              <div className="text-sm text-gray-700">{submission.description}</div>
            </div>
          )}

          {submission.notes && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Notes</label>
              <div className="text-sm text-gray-700">{submission.notes}</div>
            </div>
          )}

          {/* Tags */}
          {parseTags(submission.tags).length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Tags</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {parseTags(submission.tags).map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* AI Analysis Section */}
          {aiAnalysis && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h3 className="font-semibold text-purple-800">AI Analysis</h3>
                <span className="ml-auto text-xs text-purple-600 font-medium">
                  {aiAnalysis.confidence}% confidence
                </span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-purple-600 uppercase">AI Name</label>
                    <div className="text-slate-800">{aiAnalysis.name}</div>
                  </div>
                  {aiAnalysis.series && (
                    <div>
                      <label className="text-xs font-medium text-purple-600 uppercase">Series</label>
                      <div className="text-slate-800">{aiAnalysis.series}</div>
                    </div>
                  )}
                </div>

                {aiAnalysis.description && (
                  <div>
                    <label className="text-xs font-medium text-purple-600 uppercase">AI Description</label>
                    <div className="text-gray-700">{aiAnalysis.description}</div>
                  </div>
                )}

                {aiAnalysis.tags && aiAnalysis.tags.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-purple-600 uppercase">AI Tags</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {aiAnalysis.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-4 text-xs">
                  {aiAnalysis.isLimitedEdition && (
                    <span className="flex items-center gap-1 text-amber-700">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Limited Edition
                    </span>
                  )}
                  {aiAnalysis.isPinGroup && (
                    <span className="flex items-center gap-1 text-blue-700">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                      </svg>
                      Pin Group ({aiAnalysis.childPinCount} pins)
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Footer - button visibility keyed off the pin's real status */}
        {(() => {
          const current = submission.pin_moderation_status || submission.status;
          return (
            <div className="flex gap-2 p-4 border-t bg-gray-50 flex-wrap">
              {current !== 'approved' && (
                <button
                  onClick={() => onUpdateStatus(submission, 'approved')}
                  className="flex-1 min-w-[100px] px-3 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
                >
                  Approve
                </button>
              )}
              {current !== 'ai_approved' && (
                <button
                  onClick={() => onUpdateStatus(submission, 'ai_approved')}
                  className="flex-1 min-w-[100px] px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded-lg hover:bg-purple-600 transition-colors"
                >
                  AI Approved
                </button>
              )}
              {current !== 'pending' && (
                <button
                  onClick={() => onUpdateStatus(submission, 'pending')}
                  className="flex-1 min-w-[100px] px-3 py-2 text-sm font-medium text-gray-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
                >
                  Set Pending
                </button>
              )}
              {current !== 'rejected' && (
                <button
                  onClick={() => onUpdateStatus(submission, 'rejected')}
                  className="flex-1 min-w-[100px] px-3 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Reject
                </button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

type StatusFilter = 'all' | 'pending' | 'ai_approved' | 'approved' | 'rejected';

interface SubmissionsProps {
  lockedStatus?: Exclude<StatusFilter, 'all'>;
  title?: string;
  emptyTitle?: string;
  emptyMessage?: string;
}

export default function Submissions({
  lockedStatus,
  title = 'Submissions',
  emptyTitle,
  emptyMessage,
}: SubmissionsProps = {}) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(lockedStatus ?? 'all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const limit = 20;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset when filter or search changes
  useEffect(() => {
    setSubmissions([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
  }, [statusFilter, debouncedSearch]);

  const fetchSubmissions = useCallback(async (pageNum: number, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const statusParam = statusFilter === 'all' ? undefined : statusFilter;
      const result = await getSubmissions(pageNum, limit, statusParam, debouncedSearch || undefined);

      const newSubmissions = result.data?.submissions as Submission[] || [];
      const totalCount = result.data?.pagination?.total || 0;

      if (isLoadMore) {
        setSubmissions(prev => [...prev, ...newSubmissions]);
      } else {
        setSubmissions(newSubmissions);
      }

      setTotal(totalCount);
      setHasMore(pageNum * limit < totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [statusFilter, debouncedSearch]);

  // Initial load
  useEffect(() => {
    fetchSubmissions(1);
  }, [fetchSubmissions]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchSubmissions(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, page, fetchSubmissions]);

  const handleUpdateStatus = async (submission: Submission, status: string) => {
    if (status === 'rejected' && !confirm('Are you sure you want to reject this submission?')) return;
    try {
      await updateSubmission(submission.id, { status });
      // Update both the submission row's status (iOS-compat) and the pin's
      // real status — mirrors what the backend just wrote to D1.
      const submissionRowStatus = status === 'ai_approved' ? 'approved' : status;
      setSubmissions(prev => prev.map(s => s.id === submission.id
        ? { ...s, status: submissionRowStatus, pin_moderation_status: status }
        : s));
      setSelectedSubmission(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : `Failed to update submission status`);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      approved: 'bg-green-100 text-green-700',
      ai_approved: 'bg-purple-100 text-purple-700',
      rejected: 'bg-red-100 text-red-700',
      pending: 'bg-amber-100 text-amber-700',
    };
    return styles[status] || styles.pending;
  };

  // The submission row's `status` is the iOS-compat field. The pin's
  // `pin_moderation_status` is the truth (and can be 'ai_approved').
  // Always display the truth in the admin.
  const displayStatus = (sub: Submission): string => sub.pin_moderation_status || sub.status;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem-2rem)] sm:h-[calc(100vh-3.5rem-3rem)] lg:h-[calc(100vh-4rem)]">
      {/* Compact Header */}
      <div className="flex-shrink-0 bg-gray-100 pb-3">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-baseline gap-3">
            <h1 className="text-xl font-bold text-slate-800">{title}</h1>
            <span className="text-sm text-gray-500">
              {total.toLocaleString()} total
              {submissions.length > 0 && submissions.length < total && ` / ${submissions.length} shown`}
            </span>
          </div>
          <button
            onClick={() => { setPage(1); fetchSubmissions(1); }}
            className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search by name, SKU, or notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Status Filter Pills (hidden when locked to a single status) */}
          {!lockedStatus && (
            <div className="flex gap-1">
              {([
                { value: 'all', label: 'All' },
                { value: 'pending', label: 'Pending' },
                { value: 'ai_approved', label: 'AI Approved' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
              ] as { value: StatusFilter; label: string }[]).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setStatusFilter(value)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    statusFilter === value
                      ? 'bg-slate-700 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 bg-red-50 text-red-600 px-3 py-2 rounded text-sm mb-2">
          {error}
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {loading && submissions.length === 0 ? (
          <div className="flex items-center justify-center p-8 text-gray-500 text-sm">
            Loading submissions...
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white rounded shadow-sm p-8 text-center">
            <div className="text-3xl mb-2">{statusFilter === 'pending' || statusFilter === 'ai_approved' ? '🎉' : '📭'}</div>
            <div className="text-lg font-medium text-slate-800 mb-1">
              {emptyTitle ?? (statusFilter === 'pending' ? 'Queue Empty!' : 'No Submissions')}
            </div>
            <div className="text-sm text-gray-500">
              {emptyMessage ?? (statusFilter === 'pending' ? 'No pending submissions to review' : 'No submissions found')}
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="sm:hidden flex-1 overflow-y-auto">
              <div className="space-y-2 pb-4">
                {submissions.map((sub) => (
                  <div
                    key={sub.id}
                    className="bg-white rounded shadow-sm p-3 cursor-pointer active:bg-gray-50"
                    onClick={() => setSelectedSubmission(sub)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <SubmissionImage src={resolveImageUrl(sub.imageUrl)} name={sub.name} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <div className="font-medium text-slate-800 text-sm truncate">{sub.name || 'Untitled'}</div>
                          <div className="flex items-center gap-1">
                            {sub.ai_analysis && (
                              <span className="px-1 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">AI</span>
                            )}
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${statusBadge(displayStatus(sub))}`}>
                              {displayStatus(sub)}
                            </span>
                          </div>
                        </div>
                        {sub.sku && <code className="text-xs text-gray-500 font-mono">{sub.sku}</code>}
                        <div className="text-xs text-gray-400">{new Date(sub.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                      {displayStatus(sub) !== 'approved' && (
                        <button className="flex-1 px-2 py-1 text-xs font-medium text-white bg-green-500 rounded hover:bg-green-600" onClick={() => handleUpdateStatus(sub, 'approved')}>Approve</button>
                      )}
                      {displayStatus(sub) !== 'pending' && (
                        <button className="flex-1 px-2 py-1 text-xs font-medium text-gray-700 bg-amber-100 rounded hover:bg-amber-200" onClick={() => handleUpdateStatus(sub, 'pending')}>Pending</button>
                      )}
                      {displayStatus(sub) !== 'rejected' && (
                        <button className="flex-1 px-2 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600" onClick={() => handleUpdateStatus(sub, 'rejected')}>Reject</button>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={loadMoreRef} className="h-8 flex items-center justify-center">
                  {loadingMore && <div className="text-gray-500 text-xs">Loading more...</div>}
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:flex flex-col flex-1 bg-white rounded shadow-sm overflow-hidden min-h-0">
              {/* Table Header */}
              <div className="flex-shrink-0 bg-slate-700 text-white text-xs font-semibold uppercase tracking-wide">
                <div className="flex items-center">
                  <div className="w-11 flex-shrink-0 px-2 py-2"></div>
                  <div className="w-32 flex-shrink-0 px-2 py-2">SKU</div>
                  <div className="flex-1 min-w-0 px-2 py-2">Name</div>
                  <div className="w-12 flex-shrink-0 px-2 py-2">AI</div>
                  <div className="w-32 flex-shrink-0 px-2 py-2 hidden lg:block">Notes</div>
                  <div className="w-28 flex-shrink-0 px-2 py-2">Submitted</div>
                  <div className="w-20 flex-shrink-0 px-2 py-2">Status</div>
                  <div className="w-28 flex-shrink-0 px-2 py-2">Actions</div>
                </div>
              </div>

              {/* Scrollable Table Body */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {submissions.map((sub) => (
                  <div
                    key={sub.id}
                    className="flex items-center hover:bg-gray-50 text-sm cursor-pointer"
                    onClick={() => setSelectedSubmission(sub)}
                  >
                    <div className="w-11 flex-shrink-0 px-2 py-1.5">
                      <SubmissionImage src={resolveImageUrl(sub.imageUrl)} name={sub.name} />
                    </div>
                    <div className="w-32 flex-shrink-0 px-2 py-1.5 overflow-hidden">
                      {sub.sku ? (
                        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded font-mono text-gray-600 truncate block">{sub.sku}</code>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 px-2 py-1.5 text-slate-800 font-medium truncate">{sub.name || 'Untitled'}</div>
                    <div className="w-12 flex-shrink-0 px-2 py-1.5">
                      {sub.ai_analysis ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded" title="AI Analyzed">
                          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          AI
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">-</span>
                      )}
                    </div>
                    <div className="w-32 flex-shrink-0 px-2 py-1.5 text-gray-500 truncate hidden lg:block">{sub.notes || '-'}</div>
                    <div className="w-28 flex-shrink-0 px-2 py-1.5 text-gray-500 text-xs">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </div>
                    <div className="w-20 flex-shrink-0 px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${statusBadge(displayStatus(sub))}`}>
                        {displayStatus(sub)}
                      </span>
                    </div>
                    <div className="w-28 flex-shrink-0 px-2 py-1.5">
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        {displayStatus(sub) !== 'approved' && (
                          <button className="px-1.5 py-0.5 text-xs font-medium text-white bg-green-500 rounded hover:bg-green-600" onClick={() => handleUpdateStatus(sub, 'approved')}>OK</button>
                        )}
                        {displayStatus(sub) !== 'rejected' && (
                          <button className="px-1.5 py-0.5 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600" onClick={() => handleUpdateStatus(sub, 'rejected')}>No</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Load More Trigger */}
                <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
                  {loadingMore && <div className="text-gray-500 text-xs">Loading more...</div>}
                  {!hasMore && submissions.length > 0 && <div className="text-gray-400 text-xs">End of list</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Panel Modal */}
      {selectedSubmission && (
        <SubmissionDetailPanel
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
}
